import { Slider, Stack, Typography, colors, Badge, BadgeColorVariant, getRecipeColor } from '@ui-components/neo.tax';

import { getProjectColorVariant } from '@neo.tax/shared/utils';

import { sliderBackground } from './allocation-slider.utils';

type AllocationSliderProps = {
  allocationPercent: number;
  analyticId?: string;
  label: string;
  index?: number;
  badge?: { colorVariant: BadgeColorVariant; label: string };
} & (
  | {
      id: string;
      onChange: ({ allocationPercent, id }: { id: string; allocationPercent: number }) => void;
    }
  | {
      id?: never;
      onChange: (allocationPercent: number) => void;
    }
);

export const AllocationSlider = ({
  allocationPercent,
  analyticId,
  badge,
  id,
  index,
  label,
  onChange,
}: AllocationSliderProps): React.ReactElement => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newAllocationPercent = parseInt(e.target.value);
    if (id !== undefined) {
      onChange({ id, allocationPercent: newAllocationPercent });
      return;
    }
    onChange(newAllocationPercent);
  };

  const variant = index !== undefined ? getProjectColorVariant(index) : undefined;

  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" gap={2} alignItems="center">
        <Typography
          as="label"
          fontSize="sm"
          fontWeight="medium"
          color={variant && getRecipeColor(variant, 'whiteBg').text}
          whiteSpace="pre"
          textOverflow="truncate"
          style={{ flex: 1, minWidth: 0 }}
        >
          {variant ? '• ' : ''}
          <Typography fontSize="sm" fontWeight="medium" color={colors.gray[400]}>
            {label}
          </Typography>
        </Typography>
        {badge ? (
          // Hug the badge to its content and keep it from shrinking; the cap label can be long
          // (e.g. "Capitalized Jan 2024 - Present"), so the project name (flex: 1) truncates instead.
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <Badge dot={false} colorVariant={badge.colorVariant}>
              {badge.label}
            </Badge>
          </div>
        ) : null}
      </Stack>
      <Slider
        id={analyticId}
        border
        value={Math.round(allocationPercent)}
        onChange={handleChange}
        backgroundColors={sliderBackground({ value: allocationPercent, variant })}
      />
    </Stack>
  );
};
